import memoize from 'lodash/memoize';
import { createSelector } from 'reselect';

import { musicIdPrefixToType, THUMBPRINT_RADIO_MUSIC_ID } from '../constants/Music';

import { getArtistEntities, getAnnotationsById } from './EntityUtils';

/**
 * @param  {object} artistEntities - artist entities the redux store state
 * @param  {string} musicId - the unique id of the artist
 * @returns {Map} - an immutable Map of the artist loaded into the app
 */
export function getArtistFromState(artistEntities: $TSFixMe, musicId: string): $TSFixMe {
  return artistEntities.get(musicId) || null;
}

/**
 * @param {object} artistEntities -
 * @returns {Function} -
 * @private
 */
export function _memoizeGetArtistById(artistEntities: $TSFixMe): $TSFixMeFunction {
  return memoize(musicId => getArtistFromState(artistEntities, musicId));
}

export const createGetArtistByMusicIdSelector = createSelector(
  getArtistEntities,
  _memoizeGetArtistById,
);

/**
 * @param  {object} state - the redux store state
 * @param  {string} musicId - the unique musicId of the artist
 * @returns {Map} - an immutable Map of the artist loaded into the app
 */
export function getArtistByMusicId(state: $TSFixMe, musicId: string): $TSFixMe {
  return state.entities.get('artists').get(musicId);
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} pandoraId - the unique id of the playlist
 * @returns {List} - Playlist
 */
export function getPlaylistFromState(state: $TSFixMe, pandoraId: string): $TSFixMe {
  return state.entities.getIn(['playlists', pandoraId], null);
}

export function getPlaylistsFromState(state: $TSFixMe, pandoraIds: string): $TSFixMe {
  // @ts-expect-error TS(2339) FIXME: Property 'map' does not exist on type 'string'.
  return pandoraIds.map((playlistId: $TSFixMe) => {
    return state.entities.getIn(['playlists', playlistId], null);
  });
}

export function getTrackObjectsForPlaylist(state: $TSFixMe, pandoraId: string): $TSFixMe {
  return state.entities.getIn(['playlists', pandoraId, 'tracks'], null);
}

export function getPlaylistTotalTracks(state: $TSFixMe, pandoraId: string): $TSFixMe {
  return state.entities.getIn(['playlists', pandoraId, 'totalTracks'], 0);
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} stationId - the id of the station to search for
 * @returns {List} - Playlist if there is an automatic playlist linked to the station
 */
export function findLinkedPlaylist(state: $TSFixMe, stationId: string): $TSFixMe {
  let result = null;
  const playlists = state.entities.get('playlists');
  if (playlists) {
    result = playlists.find(
      (playlist: $TSFixMe) => playlist && playlist.get('linkedSourceId') === stationId,
    );
  }
  return result;
}

export function findTrackIndexInPlaylist(playlist: $TSFixMe, trackId: string): number {
  let result = -1;
  const tracks = playlist.get('tracks', null);
  if (tracks) {
    result = tracks.findIndex(
      (value: $TSFixMe) => value && value.get('trackPandoraId') === trackId,
    );
  }
  return result;
}

export function findMyThumbsUpPlaylist(state: $TSFixMe): $TSFixMe {
  let result = null;
  const playlists = state.entities.get('playlists');
  if (playlists) {
    result = playlists.find(
      (playlist: $TSFixMe) => playlist && playlist.get('linkedType') === 'MyThumbsUp',
    );
  }
  return result;
}

// GET GENRE
/**
 * Use case: tell the difference between a genreId (e.g. 'G100') and a genre token (e.g. 'todays-hits')
 *
 * @param {string} string - id to check
 * @returns {boolean} - true if string was a musicID, false if not (usually indicating it was a seoToken)
 */
export function isGenreMusicId(string: string): boolean {
  return /^G[0-9]+$/.test(string);
}

export function isGenrePandoraId(string: string): boolean {
  return /^GE:[0-9]+$/.test(string);
}

export function getGenreByMusicId(state: $TSFixMe, musicId: string): $TSFixMe {
  return state.entities.get('genres').get(musicId);
}

export function getGenreByToken(state: $TSFixMe, token: $TSFixMe): $TSFixMe {
  return state.entities.get('genres').find((genre: $TSFixMe) => {
    return genre.get('token') === token;
  });
}

export function getGenreByPandoraId(state: $TSFixMe, pandoraId: string): $TSFixMe {
  return state.entities
    .get('genres')
    .find((genre: $TSFixMe) => genre.get('pandoraId') === pandoraId);
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} identifier - a musicId or seoToken representing the genre
 * @returns {Map} - an immutable Map containing the genre entity
 */
export function getGenre(state: $TSFixMe, identifier: string): $TSFixMe {
  let genre = getGenreByToken(state, identifier);

  if (isGenreMusicId(identifier)) {
    genre = getGenreByMusicId(state, identifier);
  } else if (isGenrePandoraId(identifier)) {
    genre = getGenreByPandoraId(state, identifier);
  }

  return genre || getAnnotationsById(state, identifier);
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} pandoraId - the unique id of the track the lyrics belong to
 * @returns {Map} - an immutable Map of the lyrics
 */
export function getFullLyricsFromState(state: $TSFixMe, pandoraId: string): $TSFixMe {
  return state.entities.get('lyrics').get(pandoraId);
}

function isThumbprintRadio(musicId: string): boolean {
  return musicId === THUMBPRINT_RADIO_MUSIC_ID;
}

export function getTypeCodeFromMusicId(musicId: string): string {
  if (isThumbprintRadio(musicId)) {
    return 'P';
  }

  return musicId.substring(0, 1);
}

export function isOnDemandIdLike(id: string): boolean {
  const PANDORAID_REGEX = new RegExp('^[A-Z]{2,}(:)+[d]*');
  return PANDORAID_REGEX.test(id);
}

export function getNewTracksFromEdits(moves: $TSFixMe, tracks: $TSFixMe): $TSFixMe {
  if (moves && tracks) {
    const moveMap = moves.reduce((accum: $TSFixMe, move: $TSFixMe) => {
      accum[move.newIndex] = tracks.get(move.oldIndex);
      return accum;
    }, {});
    return tracks.map((track: $TSFixMe, i: $TSFixMe) => {
      if (moveMap[i]) return moveMap[i];
      return track;
    });
  }
  return tracks;
}

export function getValidInserts(inserts: $TSFixMe, tracks: $TSFixMe): $TSFixMe {
  if (inserts && tracks) {
    const newTracks = inserts.filter((insert: $TSFixMe, idx: $TSFixMe) => {
      return insert.newIndex - idx === tracks.size;
    });
    return newTracks;
  }
  return [];
}

/**
 *
 * @param {string} musicId Id from which type can be derived
 * @param {string} normalize If true, Composers are considered Artists
 * @returns {string} Music type
 */
export function getTypeFromMusicId(musicId: string, normalize = false): string {
  const typeCode = getTypeCodeFromMusicId(musicId);
  const type = musicIdPrefixToType[typeCode];
  return normalize && type === 'composer' ? 'artist' : type;
}
